import { useState } from 'react';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useAppStore, type WidgetType } from '@/lib/store';
import { useCurrentWeather } from '@/hooks/useWeather';
import { useAirQuality } from '@/hooks/useAirQuality';
import { useCurrencyRates } from '@/hooks/useCurrency';
import { useHomepageCategories } from '@/hooks/useCategories';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { TrendingUp, Cloud, Calendar, PartyPopper, RefreshCw, BookA, Leaf } from 'lucide-react';
import { useWordOfDay } from '@/hooks/useWordOfDay';
import Colors from '@/constants/colors';
import { useNextHoliday } from '@/hooks/useHolidays';
import { getTranslation } from '@/constants/translations';
import { useNavigate } from 'react-router-dom';
import PageContainer from '@/components/PageContainer';
import PageHeader from '@/components/PageHeader';
import SectionHeader from '@/components/SectionHeader';
import ContentSection from '@/components/ContentSection';
import CategoryGrid from '@/components/CategoryGrid';
import { OnlineStatusIndicator } from '@/components/OnlineStatusIndicator';
import SortableWidget from '@/components/SortableWidget';
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';

export default function Home() {
  const { language } = useAppLanguage();
  const currency = useAppStore((state) => state.currency);
  const widgetOrder = useAppStore((state) => state.widgetOrder);
  const reorderWidgets = useAppStore((state) => state.reorderWidgets);
  const getOrderedEnabledWidgets = useAppStore((state) => state.getOrderedEnabledWidgets);
  const navigate = useNavigate();
  const { pullDistance, isRefreshing, handleTouchStart, handleTouchMove, handleTouchEnd } = usePullToRefresh();
  const { vibrate } = useHapticFeedback();

  const categoriesQuery = useHomepageCategories(language);

  // Widget data from real APIs
  const currencyRatesQuery = useCurrencyRates(currency);
  const weatherQuery = useCurrentWeather();
  const airQualityQuery = useAirQuality();
  const wordQuery = useWordOfDay(language);
  const { holiday: nextHoliday, daysUntil: daysUntilHoliday, isLoading: holidaysLoading } = useNextHoliday();

  // Ordered and enabled widgets
  const orderedWidgets = getOrderedEnabledWidgets();

  // Active widget being dragged
  const [activeId, setActiveId] = useState<WidgetType | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as WidgetType);
    vibrate('medium');
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (over && active.id !== over.id) {
      vibrate('light');
      const oldIndex = widgetOrder.indexOf(active.id as WidgetType);
      const newIndex = widgetOrder.indexOf(over.id as WidgetType);
      const newOrder = arrayMove(widgetOrder, oldIndex, newIndex);
      reorderWidgets(newOrder);
    }
  };

  // Render widget content (used both in SortableWidget and DragOverlay)
  const renderWidgetContent = (widgetType: WidgetType) => {
    switch (widgetType) {
      case 'currency':
        return (
          <>
            <div style={styles.widgetHeader}>
              <TrendingUp size={20} color={Colors.primary} />
              <span style={styles.widgetTitle}>{getTranslation('currencyRate', language)}</span>
            </div>
            <div style={styles.widgetContent}>
              {currencyRatesQuery.isLoading ? (
                <div style={styles.loadingSpinner}>...</div>
              ) : currencyRatesQuery.isError ? (
                <div style={styles.widgetSubtext}>Error loading rates</div>
              ) : (
                <>
                  <div style={styles.widgetValue}>
                    {currencyRatesQuery.data?.rates['ISK']?.toFixed(2) || '—'}
                  </div>
                  <div style={styles.widgetSubtext}>
                    1 {currency} = {currencyRatesQuery.data?.rates['ISK']?.toFixed(2) || '—'} ISK
                  </div>
                </>
              )}
            </div>
          </>
        );
      case 'weather':
        return (
          <>
            <div style={styles.widgetHeader}>
              <Cloud size={20} color={Colors.primary} />
              <span style={styles.widgetTitle}>{getTranslation('weather', language)}</span>
            </div>
            <div style={styles.widgetContent}>
              {weatherQuery.isLoading ? (
                <div style={styles.loadingSpinner}>...</div>
              ) : weatherQuery.isError ? (
                <div style={styles.widgetSubtext}>Error loading weather</div>
              ) : (
                <>
                  <div style={styles.widgetValue}>
                    {weatherQuery.data?.temperature ? Math.round(weatherQuery.data.temperature) : '—'}°C
                  </div>
                  <div style={styles.widgetSubtext}>
                    {weatherQuery.data?.weatherDescription || '—'}
                  </div>
                </>
              )}
            </div>
          </>
        );
      case 'air': {
        const pm25 = airQualityQuery.data?.parameters.pm25?.value;
        return (
          <>
            <div style={styles.widgetHeader}>
              <Leaf size={20} color={Colors.primary} />
              <span style={styles.widgetTitle}>{getTranslation('air', language)}</span>
            </div>
            <div style={styles.widgetContent}>
              {airQualityQuery.isLoading ? (
                <div style={styles.loadingSpinner}>...</div>
              ) : airQualityQuery.isError ? (
                <div style={styles.widgetSubtext}>Error loading data</div>
              ) : (
                <>
                  <div style={styles.widgetValue}>
                    {pm25 !== undefined ? pm25 : '—'}
                  </div>
                  <div style={styles.widgetSubtext}>PM2.5</div>
                </>
              )}
            </div>
          </>
        );
      }
      case 'calendar':
        return (
          <>
            <div style={styles.widgetHeader}>
              <Calendar size={20} color={Colors.primary} />
              <span style={styles.widgetTitle}>{getTranslation('today', language)}</span>
            </div>
            <div style={styles.widgetContent}>
              <div style={styles.widgetValue}>{today.getDate()}</div>
              <div style={styles.widgetSubtext}>
                {today.toLocaleDateString(
                  language === 'ru' ? 'ru-RU' : language === 'pl' ? 'pl-PL' : 'en-US',
                  { month: 'long', year: 'numeric' }
                )}
              </div>
            </div>
          </>
        );
      case 'holiday':
        return (
          <>
            <div style={styles.widgetHeader}>
              <PartyPopper size={20} color={Colors.primary} />
              <span style={styles.widgetTitle}>{getTranslation('nextHoliday', language)}</span>
            </div>
            <div style={styles.widgetContent}>
              {holidaysLoading ? (
                <div style={styles.loadingSpinner}>...</div>
              ) : nextHoliday ? (
                <>
                  <div style={styles.widgetValue}>
                    {language === 'ru'
                      ? `${daysUntilHoliday} дней`
                      : language === 'pl'
                        ? `${daysUntilHoliday} dni`
                        : `${daysUntilHoliday} days`}
                  </div>
                  <div style={styles.widgetSubtext}>{nextHoliday.localName}</div>
                </>
              ) : (
                <div style={styles.widgetSubtext}>No upcoming holidays</div>
              )}
            </div>
          </>
        );
      case 'word':
        return (
          <>
            <div style={styles.widgetHeader}>
              <BookA size={20} color={Colors.primary} />
              <span style={styles.widgetTitle}>{getTranslation('wordOfDay', language)}</span>
            </div>
            <div style={styles.widgetContent}>
              {wordQuery.isLoading ? (
                <div style={styles.loadingSpinner}>...</div>
              ) : wordQuery.isError ? (
                <div style={styles.widgetSubtext}>Error loading word</div>
              ) : wordQuery.data ? (
                <>
                  <div style={styles.widgetValue}>{wordQuery.data.icelandic}</div>
                  <div style={styles.widgetSubtext}>{wordQuery.data.translation}</div>
                </>
              ) : null}
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const categories = Array.isArray(categoriesQuery.data) ? categoriesQuery.data : [];

  return (
    <PageContainer
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ position: 'relative' }}
    >
      {/* Pull-to-refresh indicator */}
      {(pullDistance > 0 || isRefreshing) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: `${Math.min(pullDistance, 60)}px`,
            backgroundColor: Colors.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            overflow: 'hidden',
          }}
        >
          <RefreshCw
            size={24}
            color="white"
            style={{
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
              opacity: Math.min(pullDistance / 60, 1),
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>

      <div style={styles.headerWrapper}>
        <PageHeader title="mland" logoSrc="/umland_u.png" />
        <div style={styles.statusIndicatorContainer}>
          <OnlineStatusIndicator />
        </div>
      </div>

      {/* Widgets Grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={orderedWidgets} strategy={rectSortingStrategy}>
          <ContentSection style={{ ...styles.widgetsGrid, padding: '0 24px' }}>
            {orderedWidgets.map((widgetType) => {
              if (widgetType === 'holiday' && !nextHoliday && !holidaysLoading) return null;
              const widgetRoutes: Record<WidgetType, string> = {
                currency: '/currency',
                weather: '/weather',
                air: '/air',
                calendar: '/calendar',
                holiday: '/holidays',
                word: '/word-of-day',
              };
              return (
                <SortableWidget
                  key={widgetType}
                  id={widgetType}
                  to={widgetRoutes[widgetType]}
                  isFullWidth={widgetType === 'word'}
                  isDragging={activeId === widgetType}
                >
                  <div style={styles.widget}>{renderWidgetContent(widgetType)}</div>
                </SortableWidget>
              );
            })}
          </ContentSection>
        </SortableContext>

        <DragOverlay>
          {activeId ? (
            <div style={{ ...styles.widget, ...styles.dragOverlay }}>
              {renderWidgetContent(activeId)}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Categories Grid */}
      {categories.length > 0 && (
        <div style={{ ...styles.section, marginTop: '32px' }}>
          <SectionHeader
            title={getTranslation('explore', language)}
          />

          <ContentSection style={{ padding: '0 24px' }}>
            <CategoryGrid
              categories={categories}
              onCategoryClick={(slug) => navigate(`/categories/${slug}`)}
            />
          </ContentSection>
        </div>
      )}


      {/* Spacing for bottom nav */}
      <div style={{ height: '40px' }} />
    </PageContainer>
  );
}

const styles = {
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: '24px',
    gap: '12px',
  } as React.CSSProperties,

  statusIndicatorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  } as React.CSSProperties,

  widgetsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  } as React.CSSProperties,

  widget: {
    backgroundColor: Colors.surface,
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    cursor: 'pointer',
    minHeight: '110px',
  } as React.CSSProperties,

  dragOverlay: {
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    transform: 'scale(1.02)',
  } as React.CSSProperties,

  widgetHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '12px',
  } as React.CSSProperties,

  widgetTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: Colors.text,
  } as React.CSSProperties,

  widgetContent: {
    gap: '4px',
  } as React.CSSProperties,

  widgetValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: Colors.primary,
  } as React.CSSProperties,

  widgetSubtext: {
    fontSize: '12px',
    color: Colors.textSecondary,
  } as React.CSSProperties,

  weatherRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  } as React.CSSProperties,

  pm25Badge: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    backgroundColor: Colors.primaryLight,
    borderRadius: '8px',
    padding: '4px 8px',
  } as React.CSSProperties,

  pm25Value: {
    fontSize: '14px',
    fontWeight: '700',
    color: Colors.text,
  } as React.CSSProperties,

  pm25Label: {
    fontSize: '8px',
    fontWeight: '500',
    color: Colors.textSecondary,
    textTransform: 'uppercase' as const,
  } as React.CSSProperties,

  loadingSpinner: {
    fontSize: '24px',
    fontWeight: '700',
    color: Colors.primary,
  } as React.CSSProperties,

  section: {
    marginBottom: '32px',
  } as React.CSSProperties,

  loadingText: {
    padding: '40px',
    textAlign: 'center' as const,
    color: Colors.textSecondary,
  } as React.CSSProperties,

  errorText: {
    padding: '40px',
    textAlign: 'center' as const,
    color: Colors.error,
  } as React.CSSProperties,

  noResults: {
    padding: '40px',
    textAlign: 'center' as const,
    color: Colors.textSecondary,
  } as React.CSSProperties,
};
